import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button } from '@mui/material';
import { AppLayout } from './components/layout/AppLayout';
import { useAuth } from './hooks/useAuth';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Lazy-load pages for code splitting
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const MaintenancePage = lazy(() => import('./pages/auth/MaintenancePage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const SearchRatesPage = lazy(() => import('./pages/booking/SearchRatesPage'));
const BookingPage = lazy(() => import('./pages/booking/BookingPage'));
const BookingConfirmPage = lazy(() => import('./pages/booking/BookingConfirmPage'));
const BookingsPage = lazy(() => import('./pages/bookings/BookingsPage'));
const ShipmentDetailPage = lazy(() => import('./pages/shipments/ShipmentDetailPage'));
const CustomsPage = lazy(() => import('./pages/customs/CustomsPage'));
const CdsShipmentsPage = lazy(() => import('./pages/customs/CdsShipmentsPage'));
const CdsShipmentDetailPage = lazy(() => import('./pages/customs/CdsShipmentDetailPage'));
const CustomsDeclarationPage = lazy(() => import('./pages/customs/CustomsDeclarationPage'));
const WalletPage = lazy(() => import('./pages/wallet/WalletPage'));
const TrackingPage = lazy(() => import('./pages/tracking/TrackingPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const InsurancePage = lazy(() => import('./pages/insurance/InsurancePage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const AddressBookPage = lazy(() => import('./pages/address-book/AddressBookPage'));
const BookingTemplatesPage = lazy(() => import('./pages/templates/BookingTemplatesPage'));
const SpendAnalyticsPage = lazy(() => import('./pages/analytics/SpendAnalyticsPage'));
const MyShipmentsPage = lazy(() => import('./pages/shipments/MyShipmentsPage'));
const LastMilePage = lazy(() => import('./pages/last-mile/LastMilePage'));
const LastMileNewPage = lazy(() => import('./pages/last-mile/LastMileNewPage'));
const LastMileTrackPage = lazy(() => import('./pages/last-mile/LastMileTrackPage'));
const AboutPage = lazy(() => import('./pages/about/AboutPage'));
const BookingFlowPage = lazy(() => import('./pages/booking/BookingFlowPage'));
const BookingHubPage = lazy(() => import('./pages/booking/BookingHubPage'));
const HsFinderPage = lazy(() => import('./pages/hs-finder/HsFinderPage'));
const PrivacyPage = lazy(() => import('./pages/privacy/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/terms/TermsPage'));
const ApiDocsPage = lazy(() => import('./pages/api-docs/ApiDocsPage'));

const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress size={40} />
  </Box>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const [termsAccepted, setTermsAccepted] = React.useState(() => localStorage.getItem('cairgo_terms_v1') === 'true');

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!termsAccepted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', p: 3 }}>
        <Paper sx={{ maxWidth: 600, p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Welcome to CAIRgo</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please review and accept our terms before continuing.
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 3, fontSize: '0.78rem', color: '#374151', lineHeight: 1.7 }}>
            <strong>CAIRgo Terms of Service (v1.0)</strong><br/><br/>
            By using CAIRgo, you agree to the following:<br/><br/>
            <strong>1. Services</strong><br/>
            CAIRgo provides customs clearance, freight booking, and last-mile delivery services. We act as your customs representative when submitting declarations to HMRC.<br/><br/>
            <strong>2. Your Responsibilities</strong><br/>
            You are responsible for the accuracy of all information you provide. Providing false or inaccurate customs information is a criminal offence under the Customs Act 2018.<br/><br/>
            <strong>3. Data Protection</strong><br/>
            We process your data in accordance with UK GDPR. Your company details, shipment information, and documents are stored securely and shared only with HMRC and relevant UK authorities as required for customs clearance.<br/><br/>
            <strong>4. Fees & Payment</strong><br/>
            Service fees, import duty, and VAT are invoiced per declaration. Payment must be received before we submit your declaration to HMRC. Outstanding invoices may delay future declarations.<br/><br/>
            <strong>5. Limitation of Liability</strong><br/>
            CAIRgo's liability is limited to the service fees paid for the relevant consignment. We are not liable for delays caused by HMRC examination, Border Force inspection, or incomplete documentation provided by you.<br/><br/>
            <strong>6. Governing Law</strong><br/>
            These terms are governed by the laws of England and Wales.
          </Box>
          <Button variant="contained" fullWidth
            onClick={() => { localStorage.setItem('cairgo_terms_v1', 'true'); setTermsAccepted(true); }}
            sx={{ textTransform: 'none', fontWeight: 700, py: 1.5, borderRadius: 2,
              background: 'linear-gradient(135deg, #10b981, #065f46)', fontSize: '1rem' }}>
            I Accept the Terms of Service
          </Button>
        </Paper>
      </Box>
    );
  }
  return <>{children}</>;
}

function PublicRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingFallback />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RootPage() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingFallback />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<RootPage />} />

          {/* Public routes */}
          {/* /login shows maintenance message; /admin-login is the hidden URL for staff */}
          <Route path="/login" element={<MaintenancePage />} />
          <Route path="/admin-login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Public pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/api-docs" element={<ApiDocsPage />} />

          {/* Public tracking pages */}
          <Route path="/track" element={<TrackingPage />} />
          <Route path="/track/:awb" element={<TrackingPage />} />
          {/* Alias: /tracking → /track */}
          <Route path="/tracking" element={<TrackingPage />} />

          {/* Protected routes - wrapped in AppLayout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Rates / booking search */}
            <Route path="rates" element={<SearchRatesPage />} />
            <Route path="booking/search" element={<SearchRatesPage />} />

            {/* New booking */}
            <Route path="booking" element={<BookingPage />} />
            <Route path="booking/new" element={<BookingPage />} />
            <Route path="book" element={<BookingHubPage />} />
            <Route path="book/flow" element={<BookingFlowPage />} />

            {/* Booking confirm */}
            <Route path="booking/confirm" element={<BookingConfirmPage />} />
            <Route path="bookings/:id/confirm" element={<BookingConfirmPage />} />

            {/* Customs declaration wizard — inside AppLayout so sidebar stays visible */}
            <Route path="customs/declare" element={<CustomsDeclarationPage />} />

            {/* Unified My Shipments — canonical list + detail */}
            <Route path="shipments" element={<MyShipmentsPage />} />
            <Route path="shipments/:id" element={<ShipmentDetailPage />} />
            {/* /bookings → redirect to /shipments; /bookings/:id kept as alias */}
            <Route path="bookings" element={<Navigate to="/shipments" replace />} />
            <Route path="bookings/:id" element={<ShipmentDetailPage />} />

            {/* Customs overview hub */}
            <Route path="customs" element={<CustomsPage />} />
            {/* Old intake pages → unified wizard */}
            <Route path="customs/new" element={<Navigate to="/customs/declare" replace />} />

            {/* CDS — HMRC Customs Declaration Service pipeline */}
            <Route path="customs/cds" element={<CdsShipmentsPage />} />
            {/* Old CDS new-shipment form → unified wizard */}
            <Route path="customs/cds/new" element={<Navigate to="/customs/declare" replace />} />
            <Route path="customs/cds/:id" element={<CdsShipmentDetailPage />} />
            <Route path="customs/intake" element={<Navigate to="/customs/declare" replace />} />

            {/* Last Mile */}
            <Route path="last-mile" element={<LastMilePage />} />
            <Route path="last-mile/new" element={<LastMileNewPage />} />
            <Route path="last-mile/:id/track" element={<LastMileTrackPage />} />

            {/* Wallet */}
            <Route path="wallet" element={<WalletPage />} />

            {/* Insurance */}
            <Route path="insurance" element={<InsurancePage />} />

            {/* Invoices */}
            <Route path="invoices" element={<InvoicesPage />} />

            {/* Support */}
            <Route path="support" element={<SupportPage />} />

            {/* Address book, templates, analytics */}
            <Route path="address-book" element={<AddressBookPage />} />
            <Route path="templates" element={<BookingTemplatesPage />} />
            <Route path="analytics" element={<SpendAnalyticsPage />} />

            {/* HS Code Finder */}
            <Route path="hs-finder" element={<HsFinderPage />} />

            {/* Profile / settings */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<ProfilePage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
